import { projectRuntimeInventoryToSidebarThreads } from "../components/sidebar/cmmdRuntimeInventoryProjection";
import { useCallback } from "react";
import { scopedThreadKey, scopeProjectRef, scopeThreadRef } from "@cmmd-forge/client-runtime";
import type { ScopedThreadRef } from "@cmmd-forge/contracts";

import { readLocalApi } from "../localApi";
import { selectSidebarThreadSummaryByRef, selectThreadByRef, useStore } from "../store";
import { useUiStateStore } from "../uiStateStore";
import { useDestructiveConfirm } from "../components/sidebar/useDestructiveConfirm";
import {
  resolveRetryPromptText,
  resolveSidebarRuntimeActions,
} from "../components/sidebar/useSidebarThreadHandlers.helpers";
import {
  buildThreadActionMenuItems,
  type ThreadActionMenuId,
} from "../components/sidebar/threadActionMenu.logic";
import { isDurablySettled } from "../components/sidebar/threadSettled";
import { runThreadMenuAction } from "../components/sidebar/sidebarThreadActions";
import type { useThreadActions } from "./useThreadActions";
import { useThreadClipboardCopiers } from "./useThreadClipboardCopiers";
import { useThreadShelfActions } from "./useThreadShelfActions";
import type { useNewThreadHandler } from "./useHandleNewThread";
import {
  cmmdRuntimeInventoryResource,
  findCmmdRuntimeInventoryThread,
} from "../cmmd/cmmdRuntimeInventoryResource";
import { resolveRuntimeIdForEnvironment } from "../cmmd/runtimeAvailabilityForEnvironment";

/**
 * The per-thread action menu, for surfaces that are not a sidebar row.
 *
 * Ported from t3code's hook of the same name, whose reason for existing is the
 * same as ours: their chat header right-click and their sidebar row show one
 * menu, so the menu cannot live inside either one. Forge's title is not in the
 * chat header — the global breadcrumb owns `project / thread` — so the second
 * surface here is the breadcrumb leaf.
 *
 * Deliberately **not** a fork of `useSidebarThreadHandlers`. It builds items
 * with the same builder and dispatches through the same
 * `runThreadMenuAction`; what it supplies differently is only what a sidebar
 * row has and a breadcrumb does not — no multi-selection, no row-level rename
 * state, no project-scoped thread map to read from.
 *
 * @module useThreadActionMenu
 */

export interface UseThreadActionMenuParams {
  /** The thread the menu acts on, or null on a route with no server thread. */
  readonly threadRef: ScopedThreadRef | null;
  readonly runtimeAvailable?: boolean;
  readonly runtimeId?: string | undefined;
  /** Fallback for "Copy path" when the thread has no worktree of its own. */
  readonly projectCwd: string | null;
  readonly confirmThreadDelete: boolean;
  readonly deleteThread: ReturnType<typeof useThreadActions>["deleteThread"];
  readonly archiveThread: ReturnType<typeof useThreadActions>["archiveThread"];
  readonly unarchiveThread: ReturnType<typeof useThreadActions>["unarchiveThread"];
  readonly settleThread: ReturnType<typeof useThreadActions>["settleThread"];
  readonly unsettleThread: ReturnType<typeof useThreadActions>["unsettleThread"];

  readonly handleNewThread: ReturnType<typeof useNewThreadHandler>["handleNewThread"];
  /** Opens this surface's own rename editor, seeded with the current title. */
  readonly startRename: (title: string) => void;
}

export interface ThreadActionMenuHandle {
  /**
   * Opens the menu at a viewport position.
   *
   * A no-op when there is no thread — a draft route has nothing to pin,
   * archive or delete, so the surface behaves as if it had no menu at all
   * rather than opening an empty or half-disabled one.
   */
  readonly openThreadActionMenu: (position: { x: number; y: number }) => Promise<void>;
  /** Mount once near this surface's root for archive and delete confirmations. */
  readonly DestructiveConfirmDialog: ReturnType<
    typeof useDestructiveConfirm
  >["DestructiveConfirmDialog"];
}

function resolveThreadMenuSettledState(
  threadRef: ScopedThreadRef,
  thread: Parameters<typeof isDurablySettled>[0] & { readonly projectId: string },
): boolean {
  const canonicalThread = findCmmdRuntimeInventoryThread(
    cmmdRuntimeInventoryResource.read().inventory,
    resolveRuntimeIdForEnvironment(threadRef.environmentId),
    thread.projectId,
    threadRef.threadId,
  );

  return canonicalThread === undefined
    ? isDurablySettled(thread)
    : canonicalThread.disposition.effectiveDisposition === "settled";
}

function prepareThreadActionMenu(threadRef: ScopedThreadRef, runtimeId?: string) {
  const api = readLocalApi();
  if (!api) return null;
  const state = useStore.getState();
  const thread =
    selectSidebarThreadSummaryByRef(state, threadRef) ??
    (runtimeId
      ? projectRuntimeInventoryToSidebarThreads(
          (cmmdRuntimeInventoryResource.read().inventory?.runtimes ?? []).filter(
            (runtime) => runtime.runtime.id === runtimeId,
          ),
          null,
        ).find((thread) => thread.id === threadRef.threadId)
      : undefined);
  if (!thread) return null;

  const threadDetail = selectThreadByRef(state, threadRef);
  return {
    api,
    retryPromptText: resolveRetryPromptText(threadDetail),
    thread,
    threadDetail,
    threadKey: scopedThreadKey(
      scopeThreadRef(thread.environmentId ?? threadRef.environmentId, threadRef.threadId),
    ),
  };
}

async function showThreadActionMenu(
  menu: NonNullable<ReturnType<typeof prepareThreadActionMenu>>,
  position: { x: number; y: number },
  threadRef: ScopedThreadRef,
  runtimeAvailable: boolean,
): Promise<ThreadActionMenuId | null> {
  return menu.api.contextMenu.show(
    buildThreadActionMenuItems({
      isPinned: Boolean(useUiStateStore.getState().pinnedThreadById[menu.threadKey]),
      isRegenerating: menu.thread.titleRegeneration != null,
      runtimeActions: resolveSidebarRuntimeActions({
        sessionStatus: menu.thread.session?.status,
        activeTurnId: menu.thread.session?.activeTurnId,
        hasRetryPrompt: menu.retryPromptText !== null,
      }),
      branch: menu.thread.branch,
      canStartNewThreadOnBranch: true,
      isArchived: menu.thread.archivedAt != null,
      archiveState: menu.thread.archive?.state ?? null,
      isSettled: resolveThreadMenuSettledState(threadRef, menu.thread),
      canUnsettle: true,
    }).map((item) =>
      !runtimeAvailable &&
      (item.id === "rename" || item.id === "regenerate-title" || item.id.startsWith("runtime-"))
        ? Object.assign({}, item, {
            disabled: true as const,
            label: `${item.label} · connection required`,
          })
        : item,
    ),
    position,
  );
}

export function useThreadActionMenu(params: UseThreadActionMenuParams): ThreadActionMenuHandle {
  const {
    threadRef,
    runtimeAvailable = true,
    runtimeId,
    projectCwd,
    confirmThreadDelete,
    deleteThread,
    archiveThread,
    unarchiveThread,
    settleThread,
    unsettleThread,
    handleNewThread,
    startRename,
  } = params;

  const destructiveConfirm = useDestructiveConfirm();
  const markThreadUnread = useUiStateStore((state) => state.markThreadUnread);
  const setThreadPinned = useUiStateStore((state) => state.setThreadPinned);
  const { copyPathToClipboard, copyBranchToClipboard, copyThreadIdToClipboard } =
    useThreadClipboardCopiers();

  const {
    attemptArchiveThread,
    attemptUnarchiveThread,
    attemptSettleThread,
    attemptUnsettleThread,
  } = useThreadShelfActions({
    archiveThread,
    unarchiveThread,
    settleThread,
    unsettleThread,
    confirmDestructiveAction: destructiveConfirm.confirm,
  });

  const openThreadActionMenu = useCallback(
    async (position: { x: number; y: number }) => {
      if (threadRef === null) return;
      const menu = prepareThreadActionMenu(threadRef, runtimeId);
      if (!menu) return;

      const clicked = await showThreadActionMenu(menu, position, threadRef, runtimeAvailable);
      if (clicked === null) return;

      await runThreadMenuAction({
        clicked,
        threadRef,
        thread: menu.thread,
        threadKey: menu.threadKey,
        threadWorkspacePath: menu.thread.worktreePath ?? projectCwd ?? null,
        retryPromptText: menu.retryPromptText,
        threadDetail: menu.threadDetail,
        confirmThreadDelete,
        copyPathToClipboard,
        copyThreadIdToClipboard,
        copyBranchToClipboard,
        startNewThreadOnBranch: (branch: string) => {
          void handleNewThread(scopeProjectRef(threadRef.environmentId, menu.thread.projectId), {
            branch,
          });
        },
        deleteThread,
        destructiveConfirm,
        markThreadUnread,
        // This surface owns ONE thread and one editor, so "which row is being
        // renamed" is not a question it has to answer — the row-keyed setter
        // the sidebar needs has nothing to do here. The title is what seeds
        // the editor, so that is the call that opens it.
        setRenamingThreadKey: () => {},
        setRenamingTitle: startRename,
        setThreadPinned,
        renamingCommittedRef: { current: false },
        attemptArchiveThread,
        attemptUnarchiveThread,
        attemptSettleThread,
        attemptUnsettleThread,
      });
    },
    [
      attemptArchiveThread,
      attemptUnarchiveThread,
      attemptSettleThread,
      attemptUnsettleThread,
      confirmThreadDelete,
      copyBranchToClipboard,
      copyPathToClipboard,
      copyThreadIdToClipboard,
      deleteThread,
      destructiveConfirm,
      handleNewThread,
      markThreadUnread,
      projectCwd,
      runtimeAvailable,
      runtimeId,
      setThreadPinned,
      startRename,
      threadRef,
    ],
  );

  return {
    openThreadActionMenu,
    DestructiveConfirmDialog: destructiveConfirm.DestructiveConfirmDialog,
  };
}
